"use client";
import { M as FieldError, N as FieldLabel, P as FieldRoot, j as FieldDescription } from "../chunks/vendor-base-ui-n0ony04mhklkmnfd.js";
import { t as cn } from "../chunks/cn-dk9nmc4mhy1765ud.js";
import { Col } from "./box.js";
import { createContext, useContext, useId, useMemo } from "react";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import IconAlertCircle from "@tabler/icons-react/dist/esm/icons/IconAlertCircle.mjs";
import { useField } from "@tanstack/react-form";
import { useSelector } from "@tanstack/react-store";
//#region src/components/field/field.tsx
var ATOMS_FIELD_VARIANTS = {};
var ATOMS_FIELD_DEFAULT_VARIANTS = null;
/**
* Carries the form instance from `<Form.Root>` down to `Field` (and the
* `Form.*` bindings). Defined here because `Field` is the primary consumer —
* `Form.Root` imports this to provide it.
*/
var FormContext = createContext(null);
/**
* Read the form provided by `<Form.Root>`, throwing a helpful error if absent.
* Shared by the connected `Field` and the `Form.*` components.
*/
function useRequiredFormContext() {
	const form = useContext(FormContext);
	if (form == null) throw new Error("A connected <Field name=…> or Form.* component must be rendered inside <Form.Root>. Wrap your form with <Form.Root form={form}>…</Form.Root>.");
	return form;
}
/**
* Structural check for a Zod object schema (exposes `.shape`). Detection is
* deliberately not an `instanceof` check so it works regardless of which copy
* of zod built the schema.
*/
function isZodObjectLike(value) {
	return typeof value === "object" && value !== null && "shape" in value;
}
/**
* Derive whether `name` is required from the form's Zod schema, so the label's
* required indicator stays in sync with the schema without a manual prop.
*
* The rule is "required unless `.optional()`": a field is optional only when its
* schema accepts `undefined` (via `.optional()`, `.nullish()`, or `.default()`),
* so a failing `safeParse(undefined)` means required. This mirrors zod's own
* documented optionality check (`schema.safeParse(undefined).success`).
*
* Only Zod object schemas (which expose `.shape` and per-field `.safeParse`)
* can be introspected. Detection is structural — not an `instanceof` check — so
* it works regardless of which copy of zod built the schema. Returns `undefined`
* when optionality can't be determined (no Zod schema, field not in the shape,
* or a function/other validator) so the caller falls back to the required
* default rather than guessing "optional"; pass `required` explicitly to override.
*/
function isRequiredInSchema(form, name) {
	const validators = form.options.validators;
	let schema;
	for (const candidate of [
		validators?.onChange,
		validators?.onSubmit,
		validators?.onBlur
	]) if (isZodObjectLike(candidate)) {
		schema = candidate;
		break;
	}
	const fieldSchema = schema?.shape?.[name];
	if (typeof fieldSchema?.safeParse !== "function") return void 0;
	try {
		return !fieldSchema.safeParse(void 0).success;
	} catch {
		return;
	}
}
/**
* Pulls user-facing error messages off a TanStack field. Handles both raw
* strings (returned by inline validators) and Zod issue objects (returned by
* `validators: { onChange: z.object(...) }` in TanStack Form v1).
*/
function extractErrorMessages(field) {
	return field.state.meta.errors.map((err) => {
		if (err == null) return null;
		if (typeof err === "string") return err;
		if (typeof err === "object" && "message" in err && typeof err.message === "string") return err.message;
		if (typeof err === "object") try {
			return JSON.stringify(err);
		} catch {
			return "Invalid value";
		}
		if (typeof err === "number" || typeof err === "boolean" || typeof err === "bigint") return String(err);
		return null;
	}).filter((msg) => msg != null && msg.length > 0);
}
function normalizeErrors(error) {
	if (!error) return [];
	return (Array.isArray(error) ? error : [error]).filter((msg) => typeof msg === "string" && msg.length > 0);
}
function FieldShell({ label, description, required, errors, invalid, className, name, htmlFor, labelMode = "control", children }) {
	const isInvalid = invalid ?? errors.length > 0;
	const uniqueErrors = [...new Set(errors)];
	const labelContent = label != null && /* @__PURE__ */ jsxs(Fragment, { children: [label, required === false ? /* @__PURE__ */ jsxs("span", {
		"aria-hidden": true,
		className: "text-od-content-tertiary",
		children: [" ", "(optional)"]
	}) : /* @__PURE__ */ jsx("span", {
		className: "sr-only",
		children: " (required)"
	})] });
	return /* @__PURE__ */ jsxs(FieldRoot, {
		name,
		invalid: isInvalid,
		className: cn("flex w-full flex-col gap-1.5", className),
		children: [
			labelContent && (labelMode === "heading" ? /* @__PURE__ */ jsx("span", {
				className: "text-label-sm text-od-content-primary",
				children: labelContent
			}) : /* @__PURE__ */ jsx(FieldLabel, {
				htmlFor,
				className: "text-label-sm text-od-content-primary",
				children: labelContent
			})),
			description != null && /* @__PURE__ */ jsx(FieldDescription, {
				className: "text-body-sm text-od-content-secondary",
				children: description
			}),
			children,
			uniqueErrors.length > 0 && /* @__PURE__ */ jsx(Col, {
				className: "gap-0.5",
				children: uniqueErrors.map((message) => /* @__PURE__ */ jsxs(FieldError, {
					match: true,
					className: "flex items-start gap-1 text-body-sm text-od-content-error",
					children: [/* @__PURE__ */ jsx(IconAlertCircle, {
						"aria-hidden": true,
						className: "h-4 w-4 shrink-0 translate-y-px"
					}), message]
				}, message))
			})
		]
	});
}
var isString = (value) => typeof value === "string";
/**
* Subscribe to a `<Form.Root>` field by name and derive its bound state: value,
* setter, blur, submit-gated errors, and schema-derived required. The connected
* `Field` and the `Form.*` bindings share this — including controls like
* `Radio.Group` that render their own label/error chrome (and so can't be
* wrapped in `Field` without double-nesting it).
*/
function useConnectedField(name, opts = {}) {
	const form = useRequiredFormContext();
	const field = useField({
		form,
		name
	});
	const generatedId = useId();
	const controlId = opts.id ?? generatedId;
	const submissionAttempts = useSelector(form.store, (state) => state.submissionAttempts);
	const errors = useMemo(() => submissionAttempts > 0 ? extractErrorMessages(field) : [], [submissionAttempts, field]);
	const derivedRequired = useMemo(() => isRequiredInSchema(form, name), [form, name]);
	const isRequired = opts.required ?? derivedRequired;
	const fieldName = isString(field.name) ? field.name : "";
	return {
		value: field.state.value,
		name: fieldName,
		id: controlId,
		setValue: (value) => field.handleChange(value),
		onBlur: field.handleBlur,
		hasError: errors.length > 0,
		errors,
		isRequired
	};
}
function FieldConnected({ name, label, labelMode, description, required, className, id, children }) {
	const state = useConnectedField(name, {
		required,
		id
	});
	return /* @__PURE__ */ jsx(FieldShell, {
		label,
		labelMode,
		description,
		required: state.isRequired,
		errors: state.errors,
		className,
		name: state.name,
		htmlFor: state.id,
		children: children(state)
	});
}
function FieldStandalone({ label, labelMode, description, required, error, invalid, className, name, id, children }) {
	return /* @__PURE__ */ jsx(FieldShell, {
		label,
		labelMode,
		description,
		required,
		errors: normalizeErrors(error),
		invalid,
		className,
		name,
		htmlFor: id,
		children
	});
}
function isFieldConnectedProps(props) {
	return typeof props.children === "function";
}
/**
* Form field wrapper: renders an accessible label above the control and error
* message(s) below, built on Base UI's Field primitive.
*
* Label association: Base UI's `Input`/`Combobox` aren't auto-associated, so in
* connected mode Field owns a control `id`, sets it as the label's `htmlFor`,
* and hands it to the render-prop child (`f.id`) — set it on the control. In
* standalone mode, pass `id` to Field and the same `id` to your control.
*
* Two modes:
* - **Connected** — pass `name` + a render-prop child. Field reads the
*   `<Form.Root>` form, subscribes to the field, derives the required state from
*   the Zod schema, and shows errors after the first submit. The `Form.*`
*   components are thin wrappers over this mode.
* - **Standalone** — pass a normal child plus explicit `error` / `required`.
*
* @example
* ```tsx
* // Connected (usually via Form.Input, but available directly):
* <Field name="email" label="Email">
*   {(f) => <Input id={f.id} value={f.value as string} onChange={(e) => f.setValue(e.target.value)} error={f.hasError} />}
* </Field>
*
* // Standalone (with a description):
* <Field label="Email" description="We’ll never share it." id="email" error={errors}>
*   <Input id="email" type="email" />
* </Field>
* ```
*/
function Field(props) {
	if (isFieldConnectedProps(props)) return /* @__PURE__ */ jsx(FieldConnected, { ...props });
	return /* @__PURE__ */ jsx(FieldStandalone, { ...props });
}
Field.displayName = "Field";
//#endregion
export { ATOMS_FIELD_DEFAULT_VARIANTS, ATOMS_FIELD_VARIANTS, Field, FormContext, useConnectedField, useRequiredFormContext };

